import { useEffect, useRef } from "react";
import { DIFFICULTY_LABELS, type ChallengeResponse } from "@versedrill/core";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { shownDifficulty } from "@/lib/game.ts";
import { formatFolioDate, formatShortDate } from "@/lib/time.ts";
import { cn } from "@/lib/utils";
import { RedactedText } from "./Redacted.tsx";
import { VersionSelect } from "./VersionSelect.tsx";

const TONE = ["text-success", "text-[color-mix(in_oklch,var(--hint)_55%,var(--foreground))]", "text-destructive", "text-destructive"] as const;

export function VerseCard({
  challenge,
  text,
  redactionOn,
  solved = false,
  score,
  onReveal,
  fromName,
}: {
  challenge: ChallengeResponse;
  text: string;
  /** Drives the difficulty label (as played) and the Reveal button. */
  redactionOn: boolean;
  /** Once solved there is nothing left to reveal, so the button goes. */
  solved?: boolean;
  score: number;
  onReveal: () => void;
  /** Sent verses: the sender's name (or null if they didn't give one). Undefined for daily games. */
  fromName?: string | null;
}) {
  const shown = shownDifficulty(challenge.difficulty, challenge.redactable, redactionOn);

  // Publish the sticky header's height so new levels can scroll into view *below* it.
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () => document.documentElement.style.setProperty("--sticky-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <header ref={ref} className="sticky top-0 z-30 border-b border-border/70 bg-background shadow-[0_10px_18px_-16px_rgb(14_23_38/0.35)]">
      <div className="mx-auto max-w-2xl px-5 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-8">
        <div className="animate-ink-in flex items-baseline justify-between gap-4 text-[0.72rem] font-ui font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {fromName !== undefined ? (
            <>
              <span>A verse for you{fromName ? <> from <span className="font-semibold text-foreground">{fromName}</span></> : null}</span>
              <VersionSelect compact />
            </>
          ) : (
            <>
              {/* The version picker lives up here, not beside the verse, so it is always in view: the verse box scrolls on a phone. */}
              <span className="flex items-center gap-2">
                <span>
                  <span className="font-display text-base normal-case tracking-normal text-brand">№</span> {challenge.number}
                </span>
                <VersionSelect compact />
              </span>
              <time dateTime={challenge.date}>
                <span className="hidden min-[440px]:inline">{formatFolioDate(challenge.date)}</span>
                <span className="min-[440px]:hidden">{formatShortDate(challenge.date)}</span>
              </time>
            </>
          )}
        </div>

        <figure className="animate-leaf-in relative mt-1 max-h-[30svh] overflow-y-auto pr-1 sm:max-h-[34svh]" style={{ ["--d" as string]: "80ms" }} tabIndex={0} aria-label="The verse to place">
          <span aria-hidden className="pointer-events-none absolute left-0 top-2 select-none font-display text-6xl leading-none text-brand/75">“</span>
          <blockquote className="pl-9 pt-2 font-body text-[1.2rem] leading-[1.55] text-pretty sm:text-[1.38rem]">
            <RedactedText text={text} />
          </blockquote>
        </figure>

        {challenge.note && <p className="mt-2 border-l-2 border-brand/60 pl-3 text-sm italic text-muted-foreground">Note: {challenge.note}</p>}

        <div className="mt-3 flex items-center justify-between gap-3 font-ui text-sm">
          <div className="flex items-center gap-2">
            <span className="sr-only text-muted-foreground min-[440px]:not-sr-only">Difficulty</span>
            <span className={cn("whitespace-nowrap font-semibold", TONE[shown])}>{DIFFICULTY_LABELS[shown]}</span>
          </div>
          {challenge.redactable && redactionOn && !solved && (
            <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2.5 text-xs" onClick={onReveal}>
              <Eye className="size-3.5" aria-hidden /> Reveal names
            </Button>
          )}
          <div className="flex items-center gap-2" aria-live="polite">
            <span className="text-muted-foreground">Score</span>
            <span className="min-w-[3ch] text-right font-semibold tabular-nums text-found">{score}</span>
          </div>
        </div>
      </div>
    </header>
  );
}
